import {validTargets} from './weightGoalModel';
import {GOAL_ASSETS} from '../cinematic/goalAssets';
import {MAX_PLAN_MONTHS, MIN_PLAN_MONTHS, METALS, planInteger} from '../cinematic/goalPlannerModel';

export const GUEST_PLAN_KEY = 'mokaab:guest-plan:v1';
export const ownerPlanKey = id => `mokaab:plan-draft:v1:${id}`;
const memory = new Map();
const unsynced = new Set();
const MAX_AGE = 24 * 60 * 60 * 1000;
const uuid = () => {
  if (globalThis.crypto.randomUUID) return globalThis.crypto.randomUUID();
  const bytes = globalThis.crypto.getRandomValues(new Uint8Array(16));
  bytes[6] = (bytes[6] & 15) | 64; bytes[8] = (bytes[8] & 63) | 128;
  return [...bytes].map((b,i) => ([4,6,8,10].includes(i)?'-':'') + b.toString(16).padStart(2,'0')).join('');
};
export function sanitizeDraft(value) {
  if (!value || !GOAL_ASSETS.some(g => g.id === value.goal)) return null;
  const text = v => String(v ?? '').slice(0,40);
  return {goal:value.goal, customName:String(value.customName || '').slice(0,60), target:text(value.target), current:text(value.current), months:text(value.months),
    metals:Array.isArray(value.metals)?Object.keys(METALS).filter(m=>value.metals.includes(m)):['silver'],
    ...(value.targetWeights?{targetWeights:Object.fromEntries(Object.entries(value.targetWeights).filter(([key])=>Object.keys(METALS).includes(key)))}:{}),
    step:Math.max(0,Math.min(4,Math.trunc(Number(value.step)||0))), visited:Math.max(0,Math.min(4,Math.trunc(Number(value.visited)||0)))};
}
export function draftPayload(draft, revision=0) {
  const d = sanitizeDraft(draft);
  if (!d) return null;
  const target = planInteger(d.target,1), current = planInteger(d.current), months = planInteger(d.months,MIN_PLAN_MONTHS,MAX_PLAN_MONTHS);
  if (target === null || current === null || months === null || !d.metals.length || (d.targetWeights&&!validTargets(d.targetWeights,d.metals))) return null;
  return {category:d.goal,title:d.customName.trim()||(d.goal==='custom'?'هدف خودم':GOAL_ASSETS.find(g=>g.id===d.goal).label),
    target_toman:target,current_toman:current,months,metals:d.metals,revision,...(d.targetWeights?{target_weights:d.targetWeights}:{})};
}
export function recordDraft(record, step=3) {
  return {goal:record.category,customName:record.title,target:String(record.target_toman),current:String(record.current_toman),months:String(record.months),metals:record.metals,...(record.target_weights?{targetWeights:record.target_weights}:{}),step,visited:4};
}
function validateEnvelope(data, expectedOwner) {
  if (!data || !Number.isFinite(data.updatedAt) || Date.now()-data.updatedAt>MAX_AGE || data.updatedAt>Date.now()+60000
      || !sanitizeDraft(data.draft) || !/^[0-9a-f]{8}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{4}-[0-9a-f]{12}$/i.test(data.id)
      || !Number.isSafeInteger(data.revision) || data.revision<0 || (expectedOwner!==undefined && String(data.ownerId)!==String(expectedOwner))) return null;
  return {...data,draft:sanitizeDraft(data.draft),queue:Array.isArray(data.queue)?data.queue.map(item=>validateEnvelope({...item,queue:[]},expectedOwner)).filter(Boolean):[]};
}
function read(key, expectedOwner) {
  let data;
  try {const raw=unsynced.has(key)?null:sessionStorage.getItem(key);data=raw?JSON.parse(raw):memory.get(key);} catch {data=memory.get(key);}
  const result=validateEnvelope(data,expectedOwner);
  if(!result)clear(key);
  return result;
}
function write(key, value) {memory.set(key,value);try {sessionStorage.setItem(key,JSON.stringify(value));unsynced.delete(key);} catch {unsynced.add(key);}return value;}
function clear(key) {memory.delete(key);try {sessionStorage.removeItem(key);unsynced.delete(key);} catch {unsynced.add(key);}}
export function readGuestPlan() {return read(GUEST_PLAN_KEY);}
export function saveGuestPlan(draft, intent=false) {
  const previous = readGuestPlan();
  return write(GUEST_PLAN_KEY,{id:previous?.id||uuid(),revision:0,draft:sanitizeDraft(draft),intent:intent||previous?.intent===true,updatedAt:Date.now()});
}
export function pendingAccountPlan(userId) {return read(ownerPlanKey(userId),userId);}
export function hasPlanToContinue(userId) {return Boolean(pendingAccountPlan(userId)||readGuestPlan()?.intent);}
export function claimGuestPlan(userId) {
  const own=pendingAccountPlan(userId),guest=readGuestPlan();
  if(!guest?.intent)return own;
  // Bind every incoming plan to the first signed-in owner; retain their earlier draft.
  const queue=own?[{...own,queue:[]},...(own.queue||[])].filter(item=>item.id!==guest.id):[];
  const result=write(ownerPlanKey(userId),{...guest,ownerId:userId,queue,updatedAt:Date.now()});
  clear(GUEST_PLAN_KEY);
  return result;
}
export function startAccountDraft(userId, record=null) {
  const result={id:record?.id||uuid(),revision:record?.revision||0,ownerId:userId,draft:record?recordDraft(record):null,updatedAt:Date.now()};
  if (result.draft) write(ownerPlanKey(userId),result);
  return result;
}
export function saveAccountDraft(userId,envelope,draft) {return write(ownerPlanKey(userId),{...envelope,ownerId:userId,draft:sanitizeDraft(draft),updatedAt:Date.now()});}
export function clearAccountDraft(userId,id) {
  const pending=pendingAccountPlan(userId);
  if(pending?.id!==id)return;
  const [next,...queue]=pending.queue||[];
  if(next)write(ownerPlanKey(userId),{...next,queue});else clear(ownerPlanKey(userId));
}
